/// <amd-module name="Feed/Legacy/Protocols/Maven/GradleConnectToFeedPanel" />
import * as React from "react";

import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageService } from "VSS/Platform/Page";

import { CiConstants, FwLinks } from "Feed/Common/Constants/Constants";
import { FeedView } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { LabelWithExternalLink } from "Feed/Legacy/Components/LabelWithExternalLink";
import { PackageMessagePanel } from "Feed/Legacy/Components/PackageMessagePanel";
import { HubWebPageDataService } from "Feed/Legacy/DataServices/WebPageDataService";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { isUserAnonymousOrPublic } from "Feed/Legacy/Helpers/PermissionHelper";
import { IConnectToFeedProps } from "Feed/Legacy/Protocols/Common/IConnectToFeedProps";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { GradleCiConstants, GradleExternalLinks, MavenCiConstants } from "Feed/Legacy/Protocols/Maven/Constants/MavenConstants";
import { IMavenRestApiUrlGeneratorService } from "Feed/Legacy/Protocols/Maven/MavenRestApiUrlGeneratorService";
import { HubAction } from "Feed/Legacy/Types/IHubState";

export interface IGradleConnectToFeedPanelProps extends IConnectToFeedProps {
    feedViews: FeedView[];
}

export interface IGradleConnectToFeedPanelState {
    authTokenCommand?: string;
    endpointUrl: string;
    selectedView: FeedView;
}

export class GradleConnectToFeedPanel extends React.Component<
    IGradleConnectToFeedPanelProps,
    IGradleConnectToFeedPanelState
> {
    private _generatedCredentials: CopyableTextField;
    private showProjectInUrl: boolean;
    private showMemberDetails: boolean;
    private isOnPrem: boolean;

    private textfieldIds = [
        "gradle-textfield-label1",
        "gradle-textfield-label2",
        "gradle-textfield-label3",
        "gradle-textfield-label4"
    ];

    constructor(props: IGradleConnectToFeedPanelProps) {
        super(props);

        const { name, view } = { ...props.feed };
        const feedId = view ? `${name}@${view.name}` : name;
        this.showProjectInUrl = props.feed && props.feed.project != null;
        const endpointUrl = this.UrlGeneratorService.getEndpointUrl(feedId, this.showProjectInUrl);
        this.showMemberDetails = isUserAnonymousOrPublic(this.props.pageContext, this.props.feed) == false;

        const pageData = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData();
        this.isOnPrem = pageData.isHosted === false;

        this.state = {
            // Decode endpoint URL so that it shows unencoded in Maven XML configuration snippets. Otherwise, copy-pasting the encoded URLs will cause
            // Maven client to re-encode the encoded URLs once more, effectively altering the feed URL.
            endpointUrl: decodeURI(endpointUrl),
            selectedView: view || null
        } as IGradleConnectToFeedPanelState;

        CustomerIntelligenceHelper.publishEvent(this.props.pageContext, CiConstants.ConnectToFeed, {
            Action: CiConstants.ConnectToFeedTabChanged,
            Protocol: MavenCiConstants.ProtocolName,
            ClientTool: GradleCiConstants.ClientTool,
            Context: HubAction[this.props.hubAction]
        });
    }

    private get UrlGeneratorService(): IMavenRestApiUrlGeneratorService {
        return this.props.pageContext.getService<IMavenRestApiUrlGeneratorService>("IMavenRestApiUrlGeneratorService");
    }

    get WebPageDataService(): HubWebPageDataService {
        return this.props.pageContext.getService<HubWebPageDataService>("IFeedWebPageDataService");
    }

    public render(): JSX.Element {
        const buildGradleSnippet = this._createSnippetForBuildGradleFile();
        const ciContext = {
            feedName: this.props.feed.name,
            protocol: MavenCiConstants.ProtocolName,
            clientTool: GradleCiConstants.ClientTool
        };
        const externalLinkAttr = {
            className: "connect-to-feed-link",
            ciContext: GradleCiConstants.GradleConnectToFeedPanel
        };

        return (
            <div className="gradle-connect-to-feed-panel connect-to-feed-panel">
                {this.state.endpointUrl ? (
                    <div className="connect-to-feed-section">
                        {this.showMemberDetails && this.renderGenerateCredentials(ciContext, externalLinkAttr)}

                        <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                            {PackageResources.GradleConnectControl_ConsumeHeadingText}
                        </div>
                        {this.showMemberDetails === false && this.renderViewDropdown()}
                        <div className="gradle-subheading" id={this.textfieldIds[1]}>
                            <FormatComponent format={PackageResources.GradleConnectControl_BuildGradleFileSettings}>
                                <span className="container-name">repositories</span>
                            </FormatComponent>
                        </div>
                        <CopyableTextField
                            pageContext={this.props.pageContext}
                            ariaDescribedByIdForLabelText={this.textfieldIds[1]}
                            text={buildGradleSnippet}
                            buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_Consume_Copy}
                            textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_Consume}
                            telemetryProperties={
                                {
                                    commandName: CiConstants.CopyCommandAddSource,
                                    ...ciContext
                                } as ICopyableTextFieldTelemetry
                            }
                        />
                        <ExternalLink
                            pageContext={this.props.pageContext}
                            href={GradleExternalLinks.BuildGradleReference}
                            {...externalLinkAttr}
                        >
                            {PackageResources.GradleConnectControl_ExternalLinkForBuildGradle}
                        </ExternalLink>
                        <ExternalLink
                            pageContext={this.props.pageContext}
                            href={GradleExternalLinks.GradleDepdendenciesReference}
                            {...externalLinkAttr}
                        >
                            {PackageResources.GradleConnectControl_ExternalLinkForGradleDependencies}
                        </ExternalLink>
                        {this.showMemberDetails &&
                            this.renderPublishSteps(buildGradleSnippet, ciContext, externalLinkAttr)}
                    </div>
                ) : (
                    <PackageMessagePanel message={PackageResources.ConnectControl_EndpointNotAvailable} />
                )}
            </div>
        );
    }

    // the location of the dropdown will change depending on whether the user is a member
    public renderViewDropdown() {
        const viewOptions: IDropdownOption[] = [];
        viewOptions.push({
            key: PackageResources.FeedViewDropdown_AllViews_Key,
            text: PackageResources.FeedViewDropdown_ConnectToFeed_AllViews_Name
        } as IDropdownOption);

        if (this.props.feedViews) {
            this.props.feedViews.forEach(view => {
                viewOptions.push({
                    key: view.id,
                    text: "@" + view.name
                } as IDropdownOption);
            });
        }

        return (
            <div>
                <div className="connect-to-feed-views-dropdown">
                    <LabelWithExternalLink
                        pageContext={this.props.pageContext}
                        labelText={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                        linkText={PackageResources.FeedViewDropdown_ConnectToFeed_Label_Link}
                        href={FwLinks.ConnectToFeedViewWhatsThis}
                        ciContext={MavenCiConstants.MavenConnectToFeedPanel}
                    />
                    <Dropdown
                        className="views-dropdown"
                        ariaLabel={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                        options={viewOptions}
                        onChanged={this._onViewDropdownChanged}
                        defaultSelectedKey={
                            this.state.selectedView
                                ? this.state.selectedView.id
                                : PackageResources.FeedViewDropdown_AllViews_Key
                        }
                    />
                </div>
            </div>
        );
    }

    public renderGenerateCredentials(ciContext, externalLinkAttr) {
        return (
            <div>
                <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                    {PackageResources.GradleConnectControl_AuthenticateHeadingText}
                </div>
                {this.renderViewDropdown()}
                <div className="gradle-subheading" id={this.textfieldIds[0]}>
                    {PackageResources.GradleConnectControl_CredentialsTitle}
                </div>
                <div>
                    <CopyableTextField
                        pageContext={this.props.pageContext}
                        ref={self => (this._generatedCredentials = self)}
                        ariaDescribedByIdForLabelText={this.textfieldIds[0]}
                        text={this.state.authTokenCommand}
                        buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_Token_Copy}
                        textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_Token}
                        telemetryProperties={
                            {
                                commandName: CiConstants.CopyCommandAuthToken,
                                ...ciContext
                            } as ICopyableTextFieldTelemetry
                        }
                    />
                    <span className="warning-text">{PackageResources.GradleConnectControl_WarningText}</span>
                </div>
                <ExternalLink
                    pageContext={this.props.pageContext}
                    href={GradleExternalLinks.GradlePropertiesReference}
                    {...externalLinkAttr}
                >
                    {PackageResources.GradleConnectControl_ExternalLinkForGradleProperties}
                </ExternalLink>
            </div>
        );
    }

    public renderPublishSteps(buildGradleSnippet, ciContext, externalLinkAttr) {
        return (
            <div>
                <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                    {PackageResources.GradleConnectControl_PublishHeadingText}
                </div>

                <div className="connect-to-feed-sub-heading" id={this.textfieldIds[2]}>
                    <FormatComponent format={PackageResources.GradleConnectControl_PublishToGradleGuidance}>
                        <span className="container-name">publishing.repositories</span>
                    </FormatComponent>
                </div>
                <CopyableTextField
                    pageContext={this.props.pageContext}
                    ariaDescribedByIdForLabelText={this.textfieldIds[2]}
                    text={buildGradleSnippet}
                    buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_Publish_Copy}
                    textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_Publish}
                    telemetryProperties={
                        {
                            commandName: CiConstants.CopyCommandAddSource,
                            ...ciContext
                        } as ICopyableTextFieldTelemetry
                    }
                />
                <ExternalLink
                    pageContext={this.props.pageContext}
                    href={GradleExternalLinks.BuildGradleReference}
                    {...externalLinkAttr}
                >
                    {PackageResources.GradleConnectControl_ExternalLinkForBuildGradle}
                </ExternalLink>
                <ExternalLink
                    pageContext={this.props.pageContext}
                    href={GradleExternalLinks.GradlePublishReference}
                    {...externalLinkAttr}
                >
                    {PackageResources.GradleConnectControl_ExternalLinkForPublish}
                </ExternalLink>

                <div className="connect-to-feed-sub-heading" id={this.textfieldIds[3]}>
                    {PackageResources.GradleConnectControl_RunCommand}
                </div>
                <CopyableTextField
                    pageContext={this.props.pageContext}
                    ariaDescribedByIdForLabelText={this.textfieldIds[3]}
                    text={"gradle publish"}
                    buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_RunCommand_Copy}
                    textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Gradle_RunCommand}
                    telemetryProperties={
                        {
                            commandName: CiConstants.CopyCommand,
                            ...ciContext
                        } as ICopyableTextFieldTelemetry
                    }
                />
            </div>
        );
    }

    private _onViewDropdownChanged = (option: IDropdownOption) => {
        let feedId = this.props.feed.name;
        let selectedView: FeedView = null;

        if (option.key !== PackageResources.FeedViewDropdown_AllViews_Key) {
            feedId += option.text;
            selectedView = { id: option.key, name: option.text } as FeedView;
        }

        this.setState({
            endpointUrl: decodeURI(this.UrlGeneratorService.getEndpointUrl(feedId, this.showProjectInUrl)),
            selectedView
        });
    };

    private _createSnippetForBuildGradleFile(): string {
        // using » to indicate where the indentations will be
        let snippet: string;
        if (this.showMemberDetails) {
            if (this.isOnPrem) {
                snippet = `maven {
                    »url '${this.state.endpointUrl}'
                    »credentials {
                    »»username "AZURE_ARTIFACTS"
                    »»password System.getenv("AZURE_ARTIFACTS_ENV_ACCESS_TOKEN") ?: "\${azureArtifactsGradleAccessToken}"
                    »}
                    »authentication {
                    »»basic(BasicAuthentication)
                    »}
                    }`;
            } else {
                snippet = `maven {
                    »url '${this.state.endpointUrl}'
                    »credentials {
                    »»username "AZURE_ARTIFACTS"
                    »»password System.getenv("AZURE_ARTIFACTS_ENV_ACCESS_TOKEN") ?: "\${azureArtifactsGradleAccessToken}"
                    »}
                    }`;
            }
        } else {
            snippet = `maven {
                »url '${this.state.endpointUrl}'
                }`;
        }

        return snippet.replace(/  +/g, "").replace(/»/g, "    ");
    }
}
